import { useEffect, useState } from "react";
import {
  Heart,
  Search,
  Store,
  X,
} from "lucide-react";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import LikedStoresTable from "./LikedStoresTable";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { RootState } from "../../../../store";
import {
  fetchLikedStores,
  unlikeStore,
} from "../../../../Redux/store_like/slice";
import "./LikedStoresPage.css";

const LikedStores = () => {
  const dispatch = useAppDispatch();
  const { likedStores, meta, loading, error } = useAppSelector(
    (state: RootState) => state.storeLike,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    dispatch(
      fetchLikedStores({
        page,
        limit,
        search: debouncedSearch || undefined,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, dateRange]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleUnlike = (targetStoreId: string) => {
    if (window.confirm("Are you sure you want to unlike this store?")) {
      dispatch(unlikeStore(targetStoreId));
    }
  };

  return (
    <section className="liked-stores-page">
      <div className="liked-stores-shell">
        <header className="liked-stores-header">
          <div className="liked-stores-heading">
            <span className="liked-stores-heading-icon">
              <Store size={22} strokeWidth={2} />
            </span>
            <div>
              <span className="liked-stores-kicker">MY STUFF</span>
              <h1>Liked Stores</h1>
              <p>
                Manage your favourite stores, view contact details and
                offers.
              </p>
            </div>
          </div>
          <span className="liked-stores-count-chip">
            {meta?.total ?? likedStores.length} stores
          </span>
        </header>

        <div className="liked-stores-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Stores per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option key={val} value={val}>
                Last {val} Entries
              </option>
            ))}
          </select>

          <div className="liked-stores-date-picker">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </div>

          <div className="liked-stores-search">
            <Search size={17} strokeWidth={2} />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search stores..."
              aria-label="Search stores"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        {loading && likedStores.length === 0 ? (
          <div className="liked-stores-desktop-empty">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="liked-stores-desktop-empty">
            <Heart size={27} strokeWidth={1.7} />
            <strong>Something went wrong</strong>
            <span>{error}</span>
          </div>
        ) : (
          <LikedStoresTable
            stores={likedStores}
            startIndex={(page - 1) * limit}
            onUnlike={handleUnlike}
          />
        )}

        {meta && meta.totalPages > 1 && (
          <Pagenation
            currentPage={page}
            totalPages={meta.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </section>
  );
};

export default LikedStores;
